<template>
  <input
    ref="input"
    v-model="internalValue"
    class="base-form-input"
    :class="{ 'is-invalid ': isInvalid }"
    :aria-invalid="isInvalid"
    :type="type"
    @invalid="setInvalid"
  />
</template>

<script>
const TYPES = [
  'text',
  'password',
  'email',
  'number',
  'url',
  'tel',
  'search',
  'range',
  'color',
  'date',
  'time',
  'datetime',
  'datetime-local',
  'month',
  'week'
];
import customModelMixin from '../mixins/custom-model';

export default {
  name: 'BaseFormInput',
  mixins: [customModelMixin],
  props: {
    type: {
      type: String,
      default: 'text',
      validator: (type) => TYPES.includes(type)
    },
    errorPattern: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      isInvalid: false,
      debounceTimer: undefined
    };
  },
  watch: {
    internalValue() {
      clearTimeout(this.debounceTimer);

      this.debounceTimer = setTimeout(() => {
        this.setCustomErrorForPattern();
      }, 500);
    }
  },
  methods: {
    setInvalid() {
      this.isInvalid = true;
    },
    setCustomErrorForPattern() {
      const input = this.$refs.input;
      if (!!input) {
        const validityState = input.validity;

        // We only set a custom error message for pattern errors
        if (validityState.patternMismatch) {
          input.setCustomValidity(this.errorPattern);
        } else {
          // As long as the error message is not empty, the form will not pass validation and will not be submitted
          input.setCustomValidity('');
        }

        this.isInvalid = !input.checkValidity();
        if (this.isInvalid) {
          // We must call the reportValidity method on the same element or nothing will happen.
          // When false is returned, cancelable invalid events are fired for each invalid child and validation problems are reported to the user.
          input.reportValidity();
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.base-form-input {
  display: block;
  @include font-size(md);
  width: 100%;
  border: 1px color(form-default-input-lines) solid;
  padding: spacing('2x') spacing('4x');
  height: auto;
  box-shadow: none;
  border-radius: rounding('1x');
  color: color(form-default-input-text);
  background: color(form-default-input-background);

  ::placeholder {
    color: color(form-default-placeholder-text);
  }

  &[disabled],
  &[readonly] {
    background-color: color(form-disabled-input-background);
    border: 1px solid color(form-disabled-input-lines);
    color: color(form-disabled-input-text);
  }

  @include generate-form-selecting-states;

  &.is-invalid {
    border-color: color(form-error-input-lines);

    &:hover {
      border-color: color(form-error-input-lines);
      box-shadow: get-form-selecting-box-shadow(error);
    }

    &:focus-visible {
      @include generate-form-elements-focus();
    }
  }

  &:hover,
  &:focus-visible {
    &[disabled],
    &[readonly] {
      box-shadow: none;
      border-color: color(form-disabled-input-lines);
      cursor: not-allowed;
    }
  }
}

::-ms-clear,
::-ms-reveal {
  display: none;
}
</style>
